import React, { useEffect, useMemo, useState } from "react";
import Nav from "../components/Nav/Nav";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { initPushNotifications } from "../services/notifications";

import "../App.css";
import "../style/OrdersHistory.css";

function OrdersHistory() {
  const navigate = useNavigate();
  const { id } = useParams();

  const {
    currentUser,
    currentRestaurant,
    getRestaurant,
    getRestaurantOrders,
    ordersList,
  } = useAuth();

  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [openNotifModal, setOpenNotifModal] = useState(false);

  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  const isIOS = /iPad|iPhone|iPod/.test(userAgent);
  const isAndroid = /Android/.test(userAgent);

  const isDigitalRestaurant = currentRestaurant?.productType === "digital";

  const getNotificationSteps = () => {
    if (isIOS) {
      return [
        "Ouvrez ce site dans Safari (pas Chrome)",
        "Cliquez sur le bouton Partager (carré avec flèche)",
        "Appuyez sur 'Ajouter à l’écran d’accueil'",
        "Ouvrez l’application depuis l’icône",
        "Activez les notifications",
      ];
    }

    if (isAndroid) {
      return [
        "Ouvrez ce site dans Google Chrome",
        "Cliquez sur 'Autoriser' quand la popup apparaît",
        "Paramètres du téléphone > Applications > Chrome",
        "Notifications > Activer",
      ];
    }

    return [
      "Utilisez Google Chrome",
      "Cliquez sur 'Autoriser' quand la popup apparaît",
      "Paramètres > Notifications > Google Chrome > Autoriser",
      "Rechargez la page",
    ];
  };

  useEffect(() => {
    getRestaurant(id);
    const unsub = getRestaurantOrders(id);

    return () => {
      if (typeof unsub === "function") unsub();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (
      currentUser &&
      currentRestaurant &&
      currentRestaurant.owner !== currentUser.uid
    ) {
      navigate("/");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, currentRestaurant]);

  useEffect(() => {
    if (
      typeof Notification !== "undefined" &&
      Notification.permission === "granted"
    ) {
      setNotificationsEnabled(true);
    }
  }, []);

  const myOrders = useMemo(() => {
    return (
      ordersList?.filter(
        (o) =>
          o.status === "paid" ||
          o.status === "completed" ||
          o.status === "delivered" ||
          o.status === "cancelled"
      ) || []
    );
  }, [ordersList]);

  const formatAmount = (value) =>
    `${(Number(value || 0) / 100).toFixed(2)} €`;

  const formatDate = (createdAt) => {
    if (!createdAt) return "";

    const date = createdAt?.seconds
      ? new Date(createdAt.seconds * 1000)
      : new Date(createdAt);

    return date.toLocaleString("fr-FR");
  };

  const getStatusLabel = (status) => {
    if (status === "paid") return "Payée";
    if (status === "completed") return "Terminée";
    if (status === "delivered") return "Livrée";
    if (status === "cancelled") return "Annulée";
    return status;
  };

  const getOrderNumber = (order) => {
    return order.id?.slice(-6)?.toUpperCase() || order.id;
  };

  return (
    <div className="one-page">
      <Nav />

      <div className="main">
        <div className="wrapper-container">
          <div className="wrapper-content">
            <div className="title-wrapper">
              <div className="title-back">
                <ArrowLeftOutlined
                  onClick={() => navigate(-1)}
                  className="back"
                />

                <p>
                  Commandes {myOrders.length > 0 ? `(${myOrders.length})` : ""}
                </p>
              </div>

              {isDigitalRestaurant && !notificationsEnabled && (
                <button
                  type="button"
                  className="button title-button-secondary"
                  onClick={() => setOpenNotifModal(true)}
                >
                  Activer les notifications
                </button>
              )}

              {isDigitalRestaurant && openNotifModal && (
                <div
                  className="notif-overlay"
                  onClick={() => setOpenNotifModal(false)}
                >
                  <div
                    className="notif-modal"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <h2 className="notif-title">Activer les notifications</h2>

                    <p className="notif-subtitle">
                      Pour recevoir les nouvelles commandes en temps réel :
                    </p>

                    <ol className="notif-list">
                      {getNotificationSteps().map((step, index) => (
                        <li key={index}>{step}</li>
                      ))}
                    </ol>

                    <div className="notif-actions">
                      <button
                        type="button"
                        className="notif-btn primary"
                        onClick={async () => {
                          if (isIOS && !window.navigator.standalone) {
                            alert(
                              "Sur iPhone, ajoutez l’app à l’écran d’accueil via Safari."
                            );
                            return;
                          }

                          const res = await initPushNotifications(id);

                          if (res.success) {
                            setNotificationsEnabled(true);
                            setOpenNotifModal(false);
                          } else if (res.reason === "denied") {
                            alert(
                              "Notifications bloquées.\n\n" +
                              "Allez dans les paramètres du navigateur et autorisez-les."
                            );
                          } else {
                            alert("Impossible d'activer les notifications");
                          }
                        }}
                      >
                        Activer les notifications
                      </button>

                      <button
                        type="button"
                        className="notif-btn secondary"
                        onClick={() => setOpenNotifModal(false)}
                      >
                        Fermer
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {myOrders.length < 1 && (
              <div className="not-found">
                <p>Aucune commande</p>
              </div>
            )}

            <div className="wrapper">
              {myOrders.map((order, index) => (
                <div
                  key={order.id}
                  className={`card-item ${index === myOrders.length - 1 ? "" : "border"
                    }`}
                >
                  <div className="orders-history-card">
                    <div className="orders-history-left">
                      <p className="orders-history-title">
                        Commande #{getOrderNumber(order)}
                      </p>

                      <p className="orders-history-price">
                        {formatAmount(order.totalAmount || order.amount)}
                      </p>

                      <p className="orders-history-sub">
                        {order.firstName || "Client"}
                      </p>

                      <p className="orders-history-sub">
                        {formatDate(order.createdAt)}
                      </p>

                      {isDigitalRestaurant ? (
                        order.customerEmail && (
                          <a
                            href={`mailto:${order.customerEmail}`}
                            className="orders-history-sub"
                          >
                            {order.customerEmail}
                          </a>
                        )
                      ) : (
                        <>
                          {order.customerPhone && (
                            <a
                              href={`tel:${order.customerPhone}`}
                              className="orders-history-sub"
                            >
                              {order.customerPhone}
                            </a>
                          )}

                          {order.customerEmail && (
                            <a
                              href={`mailto:${order.customerEmail}`}
                              className="orders-history-sub"
                            >
                              {order.customerEmail}
                            </a>
                          )}
                        </>
                      )}

                      {order.deliveryAddress && (
                        <p className="orders-history-sub">
                          {order.deliveryAddress}
                        </p>
                      )}

                      {order.tableNumber && (
                        <p className="orders-history-sub">
                          Table {order.tableNumber}
                        </p>
                      )}

                      {order.refundStatus === "partial" && (
                        <p className="orders-history-sub orders-history-payback">
                          Partiellement remboursée
                        </p>
                      )}

                      {order.refundStatus === "full" && (
                        <p className="orders-history-sub orders-history-payback">
                          Totalement remboursée
                        </p>
                      )}

                      {Number(order.refundedAmount || 0) > 0 && (
                        <p className="orders-history-sub orders-history-payback">
                          Montant remboursé :{" "}
                          {formatAmount(order.refundedAmount)}
                        </p>
                      )}
                    </div>

                    <div className="orders-history-status">
                      {getStatusLabel(order.status)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default OrdersHistory;